<template>
  <div class="container">
    <BaseListHeader title="Seznam objednávek"/>

    <div class="row my-3">
      <div class="col-auto d-flex align-items-center">
        <BaseSearchBox v-model="query.search" placeholder="Vyhledat objednávku…"/>
      </div>
    </div>
    <OrderListFilters :collapsed-default="true" v-model="filters"/>

    <div v-if="loading" class="text-muted py-3">
      Načítám…
    </div>

    <div v-else>
      <BaseDataTable
          :items="items"
          :get-row-key="(r) => r.id"
      >
        <template #head>
          <th style="width: 50px;"></th>
          <th>Číslo objednávky</th>
          <th>Číslo nabíkdy</th>
          <th>Zákazník</th>
          <th>Název kalkulace</th>
          <th>&nbsp;</th>
          <th>Vytvořeno</th>
          <th>Stav</th>
          <th>Akce</th>
        </template>

        <template #row="{ row }">
          <!-- Hlavní řádek objednávky -->
          <tr class="align-middle custom-row-height"
              :class="{ 'table-info': expandedRows.has(row.id) }"
              style="cursor: pointer;"
              @click="toggleInvoices(row.id)">
            <td>
              <div class="d-flex justify-content-center">
                <button
                    class="btn btn-outline-secondary rounded-circle p-0"
                    style="width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; border-width: 1px;"
                    @click.stop="toggleInvoices(row.id)"
                    :title="expandedRows.has(row.id) ? 'Sbalit faktury' : 'Zobrazit faktury'"
                >
                  <i class="bi" :class="expandedRows.has(row.id) ? 'bi-chevron-up' : 'bi-chevron-down'"
                     style="font-size: 10px;"></i>
                </button>
              </div>
            </td>
            <td class="fw-semibold">
              <HighlightText :text="row.order_number" :query="query.search"/>
            </td>
            <td class="text-muted">
              <HighlightText :text="row.version_tag" :query="query.search"/>
            </td>
            <td class="text-muted">
              <HighlightText :text="row.customer_name" :query="query.search"/>
            </td>
            <td class="text-muted">
              <HighlightText :text="row.calculation_name" :query="query.search"/>
            </td>
            <td>
              <span v-if="row.internal_note && row.internal_note.length > 0" v-tooltip="row.internal_note"><i
                  class="bi bi-chat-left-dots-fill text-black-50"></i>&nbsp;</span>
            </td>
            <td class="text-muted"> {{ formatDate(row.created_at) }}</td>
            <td>
              <span class="badge" :class="row.order_confirmed ? 'text-bg-success' : 'text-bg-secondary'">
                {{ row.order_confirmed ? 'potvrzená' : 'nepotvrzená' }}
              </span>
            </td>
            <td class="text-muted" @click.stop>
              <BaseActionIconButton
                  title="Zobrazit objednávku"
                  icon="bi-eye-fill"
                  variant="primary"
                  @click="viewOrder(row)"
              />
              <BaseActionIconButton
                  v-if="!row.order_confirmed"
                  title="Potvrdit objednávku"
                  icon="bi-check2-circle"
                  variant="success"
                  @click="confirmOrder(row)"
                  :disabled="row.order_confirmed"
              />
              <BaseActionIconButton
                  v-if="row.order_confirmed"
                  title="Zrušit potvrzení"
                  icon="bi-x-circle"
                  variant="danger"
                  @click="cancelConfirmOrder(row)"
              />
              <BaseActionIconButton
                  title="Upravit objednávku"
                  icon="bi-pencil-fill"
                  variant="secondary"
                  @click="editOrder(row)"
                  :disabled="row.order_confirmed"
              />
              <BaseActionIconButton
                  title="Vystavit finální fakturu"
                  icon="bi-file-earmark-check"
                  @click="openFinalInvoiceModal(row)"
                  variant="secondary"
                  :disabled="!row.order_confirmed"
              />
              <BaseActionIconButton
                  title="Zrušit objednávku"
                  icon="bi-trash3-fill"
                  variant="danger"
                  @click="cancelOrder(row)"
              />
            </td>
          </tr>

          <!-- Rozbalovací řádek s fakturami -->
          <OrderInvoicesRow
              v-if="expandedRows.has(row.id)"
              :version-id="row.id"
              :show-deleted="filters.showDeletedInvoices"
          />
        </template>
      </BaseDataTable>

      <div v-if="items.length === 0" class="text-center py-5 text-muted">
        Źádné objednávky.
      </div>

      <UniversalPagination
          v-if="items.length > 0"
          :current-page="query.page"
          :total-pages="pagination.total_pages || 1"
          :per-page="query.perPage"
          :per-page-options="perPageOptions"
          per-page-label="Položek na stránku"
          @page-changed="(p) => (query.page = p)"
          @per-page-changed="(pp) => (query.perPage = pp)"
      />
    </div>
    <OrderFinalInvoiceModal
        @invoiceSaved="createFinalInvoice"
        v-model:isVisible="isFinalInvoiceModalVisibe"
        v-model="finalInvoice"
    />
  </div>
</template>

<script setup>
import {ref, onMounted} from "vue";
import BaseListHeader from "@/components/base/BaseListHeader.vue";
import BaseSearchBox from "@/components/base/BaseSearchBox.vue";
import BaseDataTable from "@/components/base/BaseDataTable.vue";
import HighlightText from "@/components/base/HighlightText.vue";
import BaseActionIconButton from "@/components/base/BaseActionIconButton.vue";
import OrderInvoicesRow from "@/components/orders/OrderInvoicesRow.vue";
import OrderListFilters from "@/components/orders/OrderListFilters.vue";
import {showPdfFile} from "@/common/functions";

import {useServerList} from "@/composables/useServerList";
import myApi from "@/api/api";
import router from "@/router";
import UniversalPagination from "@/components/base/UniversalPagination.vue";
import OrderFinalInvoiceModal from "../components/orders/OrderFinalInvoiceModal.vue";

const expandedRows = ref(new Set());
const isFinalInvoiceModalVisibe = ref(false)

const filters = ref({
  showDeletedInvoices: false
})

const finalInvoice = ref(
    {
      version_id: null,
      delegated_vat: false,
      due: 14,
      payment_method: 'bank',
      vat_rate: null,
      price_final: null,
      line_text: null
    }
)

const toggleInvoices = (orderId) => {
  if (expandedRows.value.has(orderId)) {
    expandedRows.value.delete(orderId);
  } else {
    expandedRows.value.add(orderId);
  }
};

const fetchOrders = async ({page, perPage, search, filters}) => {
  return await myApi.orderListPaginated({
    page,
    per_page: perPage,
    search,
    deleted: filters.deleted,
  });
};

const {items, pagination, loading, query, refresh} = useServerList(fetchOrders, {
  initialPerPage: 50,
  initialSearch: "",
  initialFilters: {deleted: false},
  searchDebounceMs: 300,
});

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('cs-CZ')
}

const viewOrder = (row) => showPdfFile(row.order_pdf);

const confirmOrder = async (row) => {
  if (confirm("Opravdu chcete potvrdit objednávku?")) {
    await myApi.orderConfirm(row.id);
    await refresh();
  }
}

const cancelConfirmOrder = async (row) => {
  if (confirm("Opravdu si přejete zrušit potvrzení objednávky?")) {
    await myApi.orderUnconfirm(row.id);
    await refresh();
  }
}

const editOrder = async (row) => await router.push({name: 'newOrderView', query: {version: row.id}});

const openFinalInvoiceModal = async (row) => {
  const data = await myApi.getFinalInvoiceData(row.id)
  finalInvoice.value.version_id = row.id
  finalInvoice.value.due = 14
  finalInvoice.value.price_final = data.price
  finalInvoice.value.vat_rate = data.vat_rate
  finalInvoice.value.delegated_vat = data.delegated_vat
  finalInvoice.value.line_text = null
  finalInvoice.value.payment_method = 'bank'
  isFinalInvoiceModalVisibe.value = true
}

const cancelOrder = async (row) => {
  if (confirm("Opravdu si přejete objednávku smazat? Operace je nenávratná!")) {
    await myApi.orderDelete(row.id);
    await refresh();
  }
}

const createFinalInvoice = async () => {
  await myApi.createFinalInvoice(finalInvoice.value.version_id, finalInvoice.value);
  isFinalInvoiceModalVisibe.value = false;
  await refresh();
}

onMounted(() => {
  refresh();
});
</script>

<style scoped>
.custom-row-height {
  height: 50px;
}
</style>